<script setup>
/**
 * @description 一级分类 PK 商品卡片，复用商品列表卡片样式，仅注入 PK 操作按钮
 * @param {Object} product - 商品数据
 * @param {boolean} isAnchor - 是否为排序第一的基准商品
 * @param {boolean} selected - 是否已加入 PK
 * @param {boolean} disabled - 是否达到 PK 上限
 * @param {boolean} showCompareAction - 是否展示添加/移除比较操作
 * @emits add - 添加商品到 PK
 * @emits remove - 从 PK 移除商品
 */

import { computed } from 'vue'
import BaseButton from '~/components/base/button/Button.vue'
import StoreProductListCard from '~/components/store/productList/Card.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  isAnchor: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showCompareAction: {
    type: Boolean,
    default: true,
  },
  trackContext: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['add', 'remove'])
const { isMobile } = useMediaQuery()

const productId = computed(() => props.product?.prodId || props.product?.id)

/**
 * @description 处理 PK 按钮点击
 * @param {MouseEvent} event - 点击事件
 * @returns {void}
 */
const handleCompareClick = (event) => {
  event?.preventDefault?.()
  event?.stopPropagation?.()
  if (props.isAnchor || props.disabled) return
  if (props.selected) {
    emit('remove', productId.value)
    return
  }
  emit('add', props.product)
}
</script>

<template>
  <StoreProductListCard
    :product="product"
    :show-cart="false"
    :subtitle-line="1"
    :track-context="trackContext"
    :class="[
      'category-pk-product-card',
      {
        'category-pk-product-card--anchor': isAnchor,
        'category-pk-product-card--without-compare-action': !showCompareAction,
      },
    ]"
  >
    <template v-if="!isAnchor && showCompareAction" #price-action>
      <BaseButton
        class="category-pk-product-card__compare"
        :type="selected ? 'primary' : 'default'"
        :size="isMobile ? 'small' : 'medium'"
        :aria-label="selected ? '移除比较' : '添加比较'"
        :disabled="disabled"
        @click="handleCompareClick"
      >
        {{ selected ? '移除比较' : '添加比较' }}
      </BaseButton>
    </template>
  </StoreProductListCard>
</template>

<style lang="scss" scoped>
.category-pk-product-card {
  :deep(.product-list-card__price-row) {
    padding-right: 104px;
  }
  :deep(.product-list-card__title-row) {
    margin-bottom: 8px;
  }
  :deep(.product-list-card__info) {
    margin-bottom: 40px;
  }
  :deep(.product-list-card__model) {
    display: none;
  }

  :deep(.category-pk-product-card__compare) {
    position: absolute;
    right: 0;
    bottom: 0;
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      color 0.2s ease;
  }

  :deep(.category-pk-product-card__compare.button--type-default) {
    border-color: $border-dashed-color;
    background-color: $bg-white;
    color: $text-title-color;

    &:hover:not(:disabled):not(.button--loading) {
      border-color: $primary-color;
      background-color: $bg-white;
      color: $primary-color;
    }
  }

  :deep(.category-pk-product-card__compare.button--type-primary) {
    border-color: $primary-color;
    background-color: $primary-color;
    color: $bg-white;

    &:hover:not(:disabled):not(.button--loading) {
      border-color: $primary-color-hover;
      background-color: $primary-color-hover;
      color: $bg-white;
      opacity: 1;
    }
  }

  :deep(.category-pk-product-card__compare:disabled) {
    border-color: $border-dashed-color;
    background-color: $bg-color;
    color: $text-subtitle-color;
    cursor: not-allowed;
  }

  @include respond-to-max('lg') {
    width: 248px;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba($color-black, 0.08);

    :deep(.product-list-card__content) {
      padding-top: 16px;
    }

    :deep(.product-list-card__info) {
      margin-bottom: 18px;
    }

    :deep(.product-list-card__title) {
      margin-bottom: 8px;
      font-size: 18px;
      line-height: 26px;
    }

    :deep(.product-list-card__subtitle) {
      font-size: 14px;
      line-height: 22px;
    }

    :deep(.product-list-card__price .amount),
    :deep(.product-list-card__price .currency),
    :deep(.product-list-card__price .integer),
    :deep(.product-list-card__price .decimal) {
      font-size: 18px;
      line-height: 26px;
    }

    :deep(.product-list-card__price-row) {
      min-height: 26px;
      padding-right: 104px;
    }

    &--anchor :deep(.product-list-card__price-row) {
      padding-right: 32px;
    }

    &--without-compare-action :deep(.product-list-card__price-row) {
      padding-right: 0;
    }
  }
}
</style>
